// src/pages/KhatabookPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaSearch, FaPlus, FaTrash, FaSyncAlt } from "react-icons/fa";

const COLOR = {
  primary: "#015b9a",
  bg: "#f5f7fb",
  card: "#ffffff",
  line: "#e5e7eb",
  text: "#0f172a",
  muted: "#6b7280",
  green: "#16a34a",
  red: "#dc2626",
};

const fmtINR = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;

const KhatabookPage = () => {
  const [userId, setUserId] = useState(null);
  const [parties, setParties] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedParty, setSelectedParty] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [summary, setSummary] = useState(null);
  const [overall, setOverall] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [amount, setAmount] = useState("");
  const [flow, setFlow] = useState("in"); // "in" or "out"
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const data = JSON.parse(localStorage.getItem("userData") || "{}")?.data;
    if (!data?.id) {
      toast.error("User not found. Please login again.");
      setLoading(false);
      return;
    }
    setUserId(data.id);
  }, []);

  useEffect(() => {
    if (!userId) return;
    bootstrap();
  }, [userId]);

  const bootstrap = async () => {
    await Promise.all([fetchParties(), fetchOverall()]);
    setLoading(false);
  };

  const fetchOverall = async () => {
    try {
      const res = await fetch(
        "https://qr.billsarthi.in/public/api/khatabook/get-overall-summary",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ restaurantid: userId }),
        }
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json?.message || "Failed to load summary");
      setOverall(json?.overall_summary || null);
    } catch (e) {
      // ignore
    }
  };

  const fetchParties = async () => {
    try {
      const res = await fetch(
        "https://qr.billsarthi.in/public/api/list/partylistformaster",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ restaurantid: userId }),
        }
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json?.message || "Failed to load parties");
      setParties(json?.list || []);
    } catch (e) {
      toast.error(e.message || "Unable to load parties");
    }
  };

  const fetchLedger = async (partyId) => {
    try {
      setRefreshing(true);
      const res = await fetch("https://qr.billsarthi.in/public/api/khatabook/get", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ party_id: partyId, restaurantid: userId }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.message || "Failed to load khatabook");
      setLedger(json?.entries || []);
      setSummary(json?.summary || null);
      setSelectedParty(parties.find((p) => p.id === partyId) || null);
    } catch (e) {
      toast.error(e.message || "Unable to load khatabook");
    } finally {
      setRefreshing(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return parties;
    return parties.filter(
      (p) =>
        p.partyname?.toLowerCase().includes(q) ||
        String(p.partycontactpersonmobile || "").includes(q)
    );
  }, [parties, search]);

  const pickNumber = (obj, keys, fallback = 0) => {
    if (!obj) return fallback;
    for (const k of keys) {
      if (obj[k] !== undefined && obj[k] !== null) {
        const n = Number(obj[k]);
        if (!Number.isNaN(n)) return n;
      }
    }
    return fallback;
  };

  const handleAddEntry = async () => {
    if (!selectedParty?.id) return toast.info("Select a party first");
    if (!amount || Number(amount) <= 0) return toast.info("Enter a valid amount");
    const endpoint =
      flow === "in"
        ? "https://qr.billsarthi.in/public/api/khatabook/add-money-in"
        : "https://qr.billsarthi.in/public/api/khatabook/add-money-out";
    try {
      setSaving(true);
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          party_id: selectedParty.id,
          restaurantid: userId,
          amount: Number(amount),
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.message || "Failed to add entry");
      toast.success(`Money ${flow === "in" ? "received" : "given"}`);
      setAmount("");
      fetchLedger(selectedParty.id);
    } catch (e) {
      toast.error(e.message || "Unable to add entry");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      const res = await fetch(
        "https://qr.billsarthi.in/public/api/khatabook/delete-entry",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ entry_id: entryId, restaurantid: userId }),
        }
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json?.message || "Failed to delete");
      toast.success("Deleted");
      setLedger((prev) => prev.filter((e) => e.id !== entryId));
      fetchLedger(selectedParty.id);
    } catch (e) {
      toast.error(e.message || "Unable to delete");
    }
  };

  return (
    <Page>
      <ToastContainer position="top-right" autoClose={2200} />
      <TopStrip>
        <StripTitle>Khatabook</StripTitle>
        <StripSub>Track party-wise money in, money out and current balance.</StripSub>
        <TopStats>
          <TopStat>
            <small>Total Parties</small>
            <strong>{overall?.total_parties || parties.length || 0}</strong>
          </TopStat>
          <TopStat>
            <small>Total In</small>
            <strong style={{ color: COLOR.green }}>
              {fmtINR(pickNumber(overall, ["total_in", "total_money_in"]))}
            </strong>
          </TopStat>
          <TopStat>
            <small>Total Out</small>
            <strong style={{ color: COLOR.red }}>
              {fmtINR(pickNumber(overall, ["total_out", "total_money_out"]))}
            </strong>
          </TopStat>
          <TopStat>
            <small>Overall Balance</small>
            <strong>{fmtINR(overall?.balance || 0)}</strong>
          </TopStat>
        </TopStats>
      </TopStrip>

      <Grid>
        <Card>
          <CardHead>
            <div>
              <CardTitle>Parties</CardTitle>
              <CardSub>{filtered.length} shown</CardSub>
            </div>
            <SearchBox>
              <FaSearch />
              <input
                placeholder="Search by name or mobile"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </SearchBox>
          </CardHead>
          {loading ? (
            <Center>Loading…</Center>
          ) : (
            <PartyList>
              {filtered.map((p) => (
                <PartyRow
                  key={p.id}
                  $active={selectedParty?.id === p.id}
                  onClick={() => fetchLedger(p.id)}
                >
                  <div className="left">
                    <Avatar>{(p.partyname || "?").charAt(0).toUpperCase()}</Avatar>
                    <div>
                      <div className="name">{p.partyname}</div>
                      <div className="sub">{p.partycontactpersonmobile || "-"}</div>
                    </div>
                  </div>
                  <Arrow>›</Arrow>
                </PartyRow>
              ))}
              {!filtered.length && <Center>No parties found</Center>}
            </PartyList>
          )}
        </Card>

        <Card>
          <CardHead>
            <div>
              <CardTitle>Ledger</CardTitle>
              <CardSub>
                {selectedParty ? selectedParty.partyname : "Select a party"}
              </CardSub>
            </div>
            <PrimaryBtn
              onClick={() => selectedParty && fetchLedger(selectedParty.id)}
              disabled={!selectedParty || refreshing}
            >
              <FaSyncAlt /> Refresh
            </PrimaryBtn>
          </CardHead>

          {summary ? (
            <SummaryRow>
              <SummaryCard>
                <small>Balance</small>
                <strong>{fmtINR(pickNumber(summary, ["balance", "total_balance", "net"]))}</strong>
              </SummaryCard>
              <SummaryCard>
                <small>Money In</small>
                <strong style={{ color: COLOR.green }}>
                  {fmtINR(pickNumber(summary, ["total_in", "total_money_in", "money_in"]))}
                </strong>
              </SummaryCard>
              <SummaryCard>
                <small>Money Out</small>
                <strong style={{ color: COLOR.red }}>
                  {fmtINR(pickNumber(summary, ["total_out", "total_money_out", "money_out"]))}
                </strong>
              </SummaryCard>
            </SummaryRow>
          ) : null}

          {selectedParty ? (
            <>
              <AddRow>
                <FlowTabs>
                  <FlowTab $active={flow === "in"} onClick={() => setFlow("in")}>
                    Money In
                  </FlowTab>
                  <FlowTab $active={flow === "out"} onClick={() => setFlow("out")}>
                    Money Out
                  </FlowTab>
                </FlowTabs>
                <AmountInput
                  value={amount}
                  onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ""))}
                  placeholder="Amount"
                  inputMode="decimal"
                  pattern="[0-9.]*"
                  maxLength={10}
                />
                <PrimaryBtn onClick={handleAddEntry} disabled={saving}>
                  <FaPlus /> {saving ? "Saving..." : "Add"}
                </PrimaryBtn>
              </AddRow>

              <LedgerWrap>
                <LedgerTable>
                  <thead>
                    <tr>
                      <th style={{ width: 120 }}>Date</th>
                      <th>Notes</th>
                      <th style={{ width: 120 }}>In</th>
                      <th style={{ width: 120 }}>Out</th>
                      <th style={{ width: 86 }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledger.map((entry) => (
                      <tr key={entry.id}>
                        <td>{entry.entry_date}</td>
                        <td>{entry.notes || "—"}</td>
                        <td style={{ color: COLOR.green, fontWeight: 800 }}>
                          {entry.money_in ? `+${fmtINR(entry.money_in)}` : "—"}
                        </td>
                        <td style={{ color: COLOR.red, fontWeight: 800 }}>
                          {entry.money_out ? `-${fmtINR(entry.money_out)}` : "—"}
                        </td>
                        <td>
                          <DangerBtn onClick={() => handleDelete(entry.id)}>
                            <FaTrash /> Delete
                          </DangerBtn>
                        </td>
                      </tr>
                    ))}
                    {!ledger.length && (
                      <tr>
                        <td colSpan={5} style={{ textAlign: "center", padding: 14, color: COLOR.muted }}>
                          No entries yet for this party.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </LedgerTable>
              </LedgerWrap>
            </>
          ) : (
            <Center>Select a party to view khatabook.</Center>
          )}
        </Card>
      </Grid>
    </Page>
  );
};

export default KhatabookPage;

/* ---------------- styled ---------------- */
const Page = styled.div`
  background:
    radial-gradient(circle at 12% 10%, #edf7ff 0%, transparent 34%),
    radial-gradient(circle at 90% 0%, #e9efff 0%, transparent 40%),
    ${COLOR.bg};
  min-height: 100%;
  padding: clamp(10px, 2vw, 16px);
`;
const TopStrip = styled.div`
  border-radius: 18px;
  padding: clamp(14px, 2vw, 18px);
  background: linear-gradient(125deg, #0b4f84, #0a3f72 48%, #0f5c95);
  border: 1px solid rgba(255, 255, 255, 0.22);
  color: #fff;
  margin-bottom: 12px;
`;
const StripTitle = styled.h2`
  margin: 0;
  font-size: clamp(20px, 3vw, 28px);
  font-weight: 900;
`;
const StripSub = styled.div`
  margin-top: 4px;
  font-size: 13px;
  opacity: 0.9;
`;
const TopStats = styled.div`
  margin-top: 12px;
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
`;
const TopStat = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.24);
  background: rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  padding: 10px;
  small {
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-size: 11px;
    opacity: 0.9;
  }
  strong {
    margin-top: 4px;
    display: block;
    font-size: 1.12rem;
    color: #fff;
  }
`;
const Grid = styled.div`
  display: grid;
  grid-template-columns: 330px minmax(0, 1fr);
  gap: 12px;
  align-items: start;
  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;
const Card = styled.div`
  background: ${COLOR.card};
  border: 1px solid ${COLOR.line};
  border-radius: 18px;
  padding: 14px;
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.045);
`;
const CardHead = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  align-items: center;
  margin-bottom: 10px;
  flex-wrap: wrap;
`;
const CardTitle = styled.h3`
  margin: 0;
  color: ${COLOR.text};
`;
const CardSub = styled.div`
  color: ${COLOR.muted};
  font-size: 13px;
`;
const SearchBox = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border: 1px solid ${COLOR.line};
  border-radius: 10px;
  background: #f9f9f9;
  color: ${COLOR.text};
  input {
    border: 0;
    background: transparent;
    outline: none;
    width: 200px;
  }
  @media (max-width: 720px) {
    width: 100%;
    input {
      width: 100%;
    }
  }
`;
const PartyList = styled.div`
  display: grid;
  gap: 8px;
  max-height: 70vh;
  overflow-y: auto;
`;
const PartyRow = styled.button`
  border: 1px solid ${COLOR.line};
  border-radius: 12px;
  padding: 10px;
  background: ${({ $active }) => ($active ? "#eef2ff" : "#fff")};
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  .left {
    display: flex;
    gap: 10px;
    align-items: center;
  }
  .name {
    font-weight: 800;
    color: ${COLOR.text};
  }
  .sub {
    color: ${COLOR.muted};
    font-size: 12px;
  }
`;
const Avatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: #eef2ff;
  color: ${COLOR.primary};
  display: grid;
  place-items: center;
  font-weight: 900;
`;
const Arrow = styled.div`
  color: ${COLOR.muted};
  font-weight: 900;
`;

const SummaryRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 8px;
  margin: 10px 0;
`;
const SummaryCard = styled.div`
  background: #f8fafc;
  border: 1px solid ${COLOR.line};
  border-radius: 12px;
  padding: 10px;
  small {
    color: ${COLOR.muted};
    font-weight: 700;
    text-transform: uppercase;
  }
  strong {
    display: block;
    margin-top: 4px;
    font-size: 1.05rem;
  }
`;

const PrimaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  border-radius: 10px;
  border: none;
  background: ${COLOR.primary};
  color: #fff;
  font-weight: 800;
  cursor: pointer;
  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;
const DangerBtn = styled(PrimaryBtn)`
  background: ${COLOR.red};
`;

const AddRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  align-items: center;
  margin: 12px 0;
  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;
const FlowTabs = styled.div`
  display: inline-flex;
  gap: 6px;
  background: #eef2ff;
  padding: 6px;
  border-radius: 10px;
  border: 1px solid ${COLOR.line};
`;
const FlowTab = styled.button`
  border: none;
  background: ${({ $active }) => ($active ? "#fff" : "transparent")};
  color: ${COLOR.text};
  font-weight: 800;
  padding: 8px 10px;
  border-radius: 8px;
  cursor: pointer;
  box-shadow: ${({ $active }) => ($active ? "0 6px 12px rgba(0,0,0,0.06)" : "none")};
`;
const AmountInput = styled.input`
  border: 1px solid ${COLOR.line};
  border-radius: 10px;
  padding: 10px 12px;
  background: #f9f9f9;
  color: ${COLOR.text};
  outline: none;
`;
const LedgerWrap = styled.div`
  width: 100%;
  overflow-x: auto;
  border: 1px solid ${COLOR.line};
  border-radius: 12px;
`;

const LedgerTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  th,
  td {
    padding: 10px 12px;
    border-bottom: 1px solid ${COLOR.line};
    text-align: left;
  }
  thead {
    background: #eef2ff;
  }
  tbody tr:hover {
    background: #f9fbff;
  }
`;

const Center = styled.div`
  text-align: center;
  color: ${COLOR.muted};
  padding: 12px;
`;
